import { BiometricVault } from "./BiometricVault";
import { Storage } from "./StorageManager";
import {
  DEVICE_KEY_SERVER,
  GBRK_STORAGE_KEY,
  GBRK_EMPTY,
  INACTIVITY_TIMEOUT_MS,
  DEVICE_KEY_TTL_MS,
} from "./constants";
import { createTideKeyFromSerializedComponent } from "./utils";
import { Cryptide } from "@tideorg/js";

const {
  StringFromUint8Array,
  bytesToBase64,
  StringToUint8Array,
  base64ToBytes,
} = Cryptide.Serialization;

/* ---------------- Tunables ---------------- */
// OWASP-aligned defaults: short session, short background grace.
// Both are adjustable at runtime (see setBackgroundGraceMs).
const SESSION_GRACE_MS = 10 * 60 * 1000; // 10 minutes soft session after explicit unlock
let _backgroundGraceMs = 60 * 1000;      // 60 seconds default; updated by App Lock setting

/* ---------------- Device key cache with TTL ---------------- */
let _deviceKeyCache = {
  value: null,       // base64 string or null
  loadedAt: 0,       // Date.now()
  lastUsedAt: 0,     // Date.now()
  evictTimer: null,  // timeout id
};
let _loadPromise = null;

/* ---------------- Session + GBRK cache ---------------- */
let _session = {
  active: false,
  expireAt: 0,
  timer: null,
  backgroundTimer: null,
};

let _gbrkCache = {
  payload: null,   // { v, keys: [...] }
  loadedAt: 0,
};

function _startSession(ms = SESSION_GRACE_MS) {
  _endSessionTimersOnly();
  _session.active = true;
  _session.expireAt = Date.now() + ms;
  _session.timer = setTimeout(() => _endSession(), ms);
}
function _endSessionTimersOnly() {
  if (_session.timer) clearTimeout(_session.timer);
  if (_session.backgroundTimer) clearTimeout(_session.backgroundTimer);
  _session.timer = null;
  _session.backgroundTimer = null;
}
function _endSession() {
  _endSessionTimersOnly();
  _session.active = false;
  _session.expireAt = 0;
  _gbrkCache.payload = null;
  _gbrkCache.loadedAt = 0;
  _setDeviceKeyCache(null);
}
function _isSessionActive() {
  if (!_session.active) return false;
  if (Date.now() > _session.expireAt) {
    _endSession();
    return false;
  }
  return true;
}

/* ---------------- Device key eviction helpers ---------------- */
function _clearEvictTimer() {
  if (_deviceKeyCache.evictTimer) {
    clearTimeout(_deviceKeyCache.evictTimer);
    _deviceKeyCache.evictTimer = null;
  }
}
function _scheduleEviction(ms = DEVICE_KEY_TTL_MS) {
  _clearEvictTimer();
  _deviceKeyCache.evictTimer = setTimeout(() => {
    _deviceKeyCache.value = null;
    _deviceKeyCache.loadedAt = 0;
    _deviceKeyCache.lastUsedAt = 0;
    _clearEvictTimer();
  }, ms);
}
function _setDeviceKeyCache(val) {
  _deviceKeyCache.value = val || null;
  _deviceKeyCache.loadedAt = val ? Date.now() : 0;
  _deviceKeyCache.lastUsedAt = _deviceKeyCache.loadedAt;
  if (val) _scheduleEviction(DEVICE_KEY_TTL_MS);
  else _clearEvictTimer();
  return val;
}
function _isCacheExpired() {
  if (!_deviceKeyCache.value) return true;
  const now = Date.now();
  const age = now - _deviceKeyCache.loadedAt;
  const idle = now - _deviceKeyCache.lastUsedAt;
  return age > DEVICE_KEY_TTL_MS || idle > INACTIVITY_TIMEOUT_MS;
}
// Non-prompting peek
function _peekCachedDeviceKey() {
  if (!_deviceKeyCache.value || _isCacheExpired()) return null;
  _deviceKeyCache.lastUsedAt = Date.now();
  _scheduleEviction(DEVICE_KEY_TTL_MS);
  return _deviceKeyCache.value;
}

/* ---------------- Key retrieval ---------------- */
async function getCachedDeviceKey() {
  if (!_deviceKeyCache.value || _isCacheExpired()) {
    _setDeviceKeyCache(null);
    const hasDeviceKey = await KeyManager.hasDeviceKey(); // silent presence
    if (!hasDeviceKey) return null;
    return await KeyManager.loadDeviceKey(); // may prompt if needed
  }
  _deviceKeyCache.lastUsedAt = Date.now();
  _scheduleEviction(DEVICE_KEY_TTL_MS);
  return _deviceKeyCache.value;
}

/* ---------------- Vault access helpers ---------------- */
// Silent read: only succeeds when the vault can decrypt without prompting.
async function _vaultGetSilent() {
  try {
    const creds = await BiometricVault.getCredentials({ server: DEVICE_KEY_SERVER, interactive: false });
    return creds?.password || null;
  } catch {
    return null;
  }
}
// Interactive read: runs the biometric/PIN ceremony when required.
async function _vaultGetInteractive() {
  const creds = await BiometricVault.getCredentials({ server: DEVICE_KEY_SERVER, interactive: true });
  if (!creds || !creds.password) throw new Error("No device key found");
  return creds.password;
}

/* ---------------- GBRK crypto helpers ---------------- */
const gbrkEnc = async (deviceKeyBase64, msg) => {
  const key = createTideKeyFromSerializedComponent(deviceKeyBase64);
  const encMsg = await key.asymmetricEncrypt(StringToUint8Array(msg));
  return bytesToBase64(encMsg);
};
const gbrkDec = async (deviceKeyBase64, encMsg) => {
  const key = createTideKeyFromSerializedComponent(deviceKeyBase64);
  const msg = await key.asymmetricDecrypt(base64ToBytes(encMsg));
  return StringFromUint8Array(msg);
};

/* ---------------- GBRK storage helpers (JSON) ---------------- */
const readGBRK = async (deviceKeyBase64) => {
  const encMsg = await Storage.get(GBRK_STORAGE_KEY);
  if (!encMsg) return { v: GBRK_EMPTY?.v, keys: [] };
  try {
    const plain = await gbrkDec(deviceKeyBase64, encMsg);
    const obj = JSON.parse(plain);
    return { v: obj.v, keys: obj.keys.slice() };
  } catch {
    return { v: GBRK_EMPTY?.v, keys: [] };
  }
};
// Session-friendly cached read
async function readGBRKCached(deviceKeyBase64) {
  if (_gbrkCache.payload && _isSessionActive() && Date.now() - _gbrkCache.loadedAt < SESSION_GRACE_MS) {
    const { v, keys } = _gbrkCache.payload;
    return { v, keys: keys.slice() };
  }
  const payload = await readGBRK(deviceKeyBase64);
  _gbrkCache.payload = { v: payload.v, keys: payload.keys.slice() };
  _gbrkCache.loadedAt = Date.now();
  return { v: payload.v, keys: payload.keys.slice() };
}
const writeGBRK = async (deviceKeyBase64, msg) => {
  const encMsg = await gbrkEnc(deviceKeyBase64, JSON.stringify(msg));
  await Storage.set(GBRK_STORAGE_KEY, encMsg);
  _gbrkCache.payload = { v: msg.v, keys: msg.keys.slice() };
  _gbrkCache.loadedAt = Date.now();
  return true;
};

/* ---------------- KeyManager ---------------- */
const KeyManager = {
  _biometricInFlight: false,

  // Session API
  isSessionActive: _isSessionActive,
  sessionExpiresAt: () => _session.expireAt,
  extendSession(ms = SESSION_GRACE_MS) { if (_isSessionActive()) _startSession(ms); },

  // Non-prompting peek
  peekCachedDeviceKey: _peekCachedDeviceKey,

  // Load device key:
  // - forcePrompt=true -> always verify identity (use on app-load or after idle)
  // - otherwise: try silent first, prompt if needed
  async loadDeviceKey(options = {}) {
    const { forcePrompt = false } = options;
    if (_loadPromise) return _loadPromise;

    _loadPromise = (async () => {
      // If we already have an active session and not forcing, return cached/silent
      if (!forcePrompt && _isSessionActive()) {
        const cached = _peekCachedDeviceKey();
        if (cached) return cached;
        const silent = await _vaultGetSilent();
        if (silent) { _startSession(SESSION_GRACE_MS); return _setDeviceKeyCache(silent); }
      }

      // If not forcing, try silent first
      if (!forcePrompt) {
        const silent = await _vaultGetSilent();
        if (silent) { _startSession(SESSION_GRACE_MS); return _setDeviceKeyCache(silent); }
      }

      // Prompt (first open with key or after idle)
      KeyManager._biometricInFlight = true;
      try {
        const password = await _vaultGetInteractive();
        _startSession(SESSION_GRACE_MS);
        return _setDeviceKeyCache(password);
      } finally {
        KeyManager._biometricInFlight = false;
      }
    })().finally(() => { _loadPromise = null; });

    return _loadPromise;
  },

  // Explicit helper when you want a prompt now
  async unlockNow() { return this.loadDeviceKey({ forcePrompt: true }); },

  // Returns cached key if within TTL; else may prompt
  getCachedDeviceKey,

  /* ---------- Device Key CRUD ---------- */
  async hasDeviceKey() {
    try {
      // Silent presence check - no prompt needed to know if creds exist
      return await BiometricVault.hasCredentials({ server: DEVICE_KEY_SERVER });
    } catch { return false; }
  },

  async storeDeviceKey(deviceKey) {
    if (!deviceKey) throw new Error("Device key is required");
    await BiometricVault.setCredentials({ username: "device", password: deviceKey, server: DEVICE_KEY_SERVER });
    _setDeviceKeyCache(deviceKey);
    _startSession(SESSION_GRACE_MS);
    return true;
  },

  async deleteDeviceKey() {
    try { await BiometricVault.deleteCredentials({ server: DEVICE_KEY_SERVER }); } catch {}
    _endSession();
  },

  // Wipes local auth state & caches (kept separate from app-specific keys)
  async wipeAllLocal() {
    try { await this.deleteDeviceKey(); } catch {}
    try { await BiometricVault.wipe(); } catch {}
    try { await Storage.remove?.("user_profile"); } catch {}
    try { await Storage.remove?.(GBRK_STORAGE_KEY); } catch {}
    try { await Storage.set("user_profile", ""); } catch {}
    try { await Storage.set(GBRK_STORAGE_KEY, ""); } catch {}
    _endSession();
  },

  /* ---------- One-time user verification for first-run profile ---------- */
  async verifyIdentityOnly() {
    KeyManager._biometricInFlight = true;
    try {
      await BiometricVault.verifyIdentity();
      _startSession(SESSION_GRACE_MS);
      return true;
    } finally {
      KeyManager._biometricInFlight = false;
    }
  },

  /* ---------- User Profile ---------- */
  async getUserInfo(key = null) {
    const userProfile = await Storage.get("user_profile");
    if (!userProfile) return key != null ? null : "{}";
    try {
      const obj = JSON.parse(userProfile);
      return key != null ? obj[key] : userProfile;
    } catch { return key != null ? null : "{}"; }
  },
  async storeUserInfo({ user } = {}) {
    if (user) await Storage.set("user_profile", JSON.stringify(user));
    return true;
  },
  async setUserInfo(key, value) {
    const current = await this.getUserInfo();
    let obj = {};
    try { obj = JSON.parse(current || "{}"); } catch { obj = {}; }
    obj[key] = value;
    await Storage.set("user_profile", JSON.stringify(obj));
    return true;
  },

  /* ---------- GBRK List (single encrypted blob; session-cached) ---------- */
  async hasGBRK(key, opts = {}) {
    const deviceKey = opts.deviceKey ?? _peekCachedDeviceKey();
    if (!deviceKey) return false;
    const payload = await readGBRKCached(deviceKey);
    return payload.keys.includes(String(key));
  },
  async addGBRK(key, opts = {}) {
    const deviceKey = opts.deviceKey ?? (await getCachedDeviceKey());
    const payload = await readGBRKCached(deviceKey);
    const exists = payload.keys.includes(String(key));
    const next = exists ? payload : { ...payload, keys: [...payload.keys, String(key)] };
    if (next !== payload) await writeGBRK(deviceKey, next);
    return true;
  },
  async removeGBRK(key, opts = {}) {
    const deviceKey = opts.deviceKey ?? (await getCachedDeviceKey());
    const payload = await readGBRKCached(deviceKey);
    const exists = payload.keys.includes(String(key));
    const next = exists ? { ...payload, keys: payload.keys.filter((k) => k !== String(key)) } : payload;
    if (next !== payload) await writeGBRK(deviceKey, next);
    return true;
  },
  async listGBRK(opts = {}) {
    const deviceKey = opts.deviceKey ?? (await getCachedDeviceKey());
    const payload = await readGBRKCached(deviceKey);
    return payload.keys.slice();
  },

  /* ---------- Settings hooks ---------- */
  setBackgroundGraceMs(ms) {
    const val = Math.max(0, ms | 0);
    _backgroundGraceMs = val;
    // If already backgrounded with a pending timer, restart it to honor the new grace
    if (_session.backgroundTimer) {
      clearTimeout(_session.backgroundTimer);
      _session.backgroundTimer = setTimeout(() => { _endSession(); }, _backgroundGraceMs);
    }
    return _backgroundGraceMs;
  },
  getBackgroundGraceMs() { return _backgroundGraceMs; },
};

export { KeyManager };

/* ---------------- Background/session wiring ---------------- */
(function wireSessionAndEviction() {
  try {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          // If a biometric prompt is on-screen, do not kill the session mid-flow
          if (KeyManager._biometricInFlight) return;
          if (_session.backgroundTimer) clearTimeout(_session.backgroundTimer);
          // OWASP: short background grace, then end session so a prompt will be required on resume
          _session.backgroundTimer = setTimeout(() => { _endSession(); }, _backgroundGraceMs);
        } else if (document.visibilityState === "visible") {
          if (_session.backgroundTimer) { clearTimeout(_session.backgroundTimer); _session.backgroundTimer = null; }
        }
      });
    }
  } catch {}
})();
